import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { isTwoFactorChallenge } from '../api/auth';
import type { TwoFactorChallengeResponse } from '../api/auth';
import { TwoFactorVerifyModal, TwoFactorRecoveryModal } from '../components/two-factor';
import { ArrowRight } from 'lucide-react';

type TwoFactorMode = 'authenticator' | 'recovery' | null;

export function Login() {
  const navigate = useNavigate();
  const { login, loginWithUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallengeResponse | null>(null);
  const [twoFactorMode, setTwoFactorMode] = useState<TwoFactorMode>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await login(email, password);

      if (isTwoFactorChallenge(response)) {
        setTwoFactorChallenge(response);
        setTwoFactorMode('authenticator');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSuccess = (user: { id: string; email: string; name: string; role: string }, csrfToken: string) => {
    loginWithUser(user, csrfToken);
    setTwoFactorChallenge(null);
    setTwoFactorMode(null);
    navigate('/');
  };

  const handleTwoFactorCancel = () => {
    setTwoFactorChallenge(null);
    setTwoFactorMode(null);
    setPassword('');
  };

  return (
    <div className="flex min-h-screen bg-surface-secondary">
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="flex items-start gap-1.5">
              <img
                src="/sequesto-logo-dark.png"
                alt="SEQUESTO"
                className="h-4 w-auto select-none"
                draggable={false}
              />
              <span className="eyebrow mt-0.5">OS</span>
            </div>
            <h1 className="mt-8 font-display text-display-sm text-text-primary">
              Welcome back
            </h1>
            <p className="mt-2 text-body-md text-text-tertiary">
              Sign in to your account to continue
            </p>
          </div>

          <div className="rounded-3xl bg-surface-primary p-8 border border-border-light shadow-soft">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Input
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
                rightIcon={
                  !isLoading && (
                    <ArrowRight className="h-4 w-4" />
                  )
                }
              >
                Sign in
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-body-xs text-text-muted">
            Internal use only. Contact your administrator for access.
          </p>
        </div>
      </div>

      {twoFactorChallenge && twoFactorMode === 'authenticator' && (
        <TwoFactorVerifyModal
          isOpen={true}
          challengeToken={twoFactorChallenge.challengeToken}
          expiresAt={twoFactorChallenge.expiresAt}
          onSuccess={handleTwoFactorSuccess}
          onCancel={handleTwoFactorCancel}
          onUseRecoveryCode={() => setTwoFactorMode('recovery')}
        />
      )}

      {twoFactorChallenge && twoFactorMode === 'recovery' && (
        <TwoFactorRecoveryModal
          isOpen={true}
          challengeToken={twoFactorChallenge.challengeToken}
          expiresAt={twoFactorChallenge.expiresAt}
          onSuccess={handleTwoFactorSuccess}
          onCancel={handleTwoFactorCancel}
          onUseAuthenticator={() => setTwoFactorMode('authenticator')}
        />
      )}
    </div>
  );
}
